import React, { useState, useEffect, useCallback } from 'react';
import { useApp } from '../context/AppContext';
import { createCategory, updateCategory, getCategories } from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Checkbox } from '../components/ui/checkbox';
import { toast } from 'sonner';
import { Tag, Plus, Edit, Check, X, Box } from 'lucide-react';

export default function Categories() {
  const { refreshCategories } = useApp();
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('active');
  const [showModal, setShowModal] = useState(false);
  const [editingCategory, setEditingCategory] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    is_active: true,
    allowed_relationship_types: ['STANDALONE', 'PARENT', 'CHILD']
  });

  const loadCategories = useCallback(async () => {
    try {
      const res = await getCategories();
      setCategories(res.data);
    } catch (error) {
      toast.error('Failed to load categories');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadCategories();
  }, [loadCategories]);

  const activeCategories = categories.filter(c => c.is_active !== false);
  const inactiveCategories = categories.filter(c => c.is_active === false);

  const resetForm = () => {
    setFormData({
      name: '',
      is_active: true,
      allowed_relationship_types: ['STANDALONE', 'PARENT', 'CHILD']
    });
    setEditingCategory(null);
  };

  const handleOpenModal = (category = null) => {
    if (category) {
      setEditingCategory(category);
      setFormData({
        name: category.name,
        is_active: category.is_active !== false,
        allowed_relationship_types: category.allowed_relationship_types || ['STANDALONE', 'PARENT', 'CHILD']
      });
    } else {
      resetForm();
    }
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    resetForm();
  };

  const handleSubmit = async () => {
    if (!formData.name.trim()) {
      toast.error('Category name is required');
      return;
    }

    try {
      if (editingCategory) {
        await updateCategory(editingCategory.id, formData);
        toast.success('Category updated');
      } else {
        await createCategory(formData);
        toast.success('Category created');
      }
      handleCloseModal();
      loadCategories();
      refreshCategories();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save category');
    }
  };

  const toggleRelationshipType = (type) => {
    const current = formData.allowed_relationship_types || [];
    if (current.includes(type)) {
      setFormData({
        ...formData,
        allowed_relationship_types: current.filter(t => t !== type)
      });
    } else {
      setFormData({
        ...formData,
        allowed_relationship_types: [...current, type]
      });
    }
  };

  const handleDeactivate = async (category) => {
    try {
      await updateCategory(category.id, { ...category, is_active: false });
      toast.success('Category deactivated');
      loadCategories();
      refreshCategories();
    } catch (error) {
      toast.error('Failed to deactivate category');
    }
  };

  const handleReactivate = async (category) => {
    try {
      await updateCategory(category.id, { ...category, is_active: true });
      toast.success('Category reactivated');
      loadCategories();
      refreshCategories();
    } catch (error) {
      toast.error('Failed to reactivate category');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Tag className="h-6 w-6 text-blue-600" />
          <div>
            <h1 className="text-2xl font-bold">Category Management</h1>
            <p className="text-gray-500 text-sm">Add and edit asset categories</p>
          </div>
        </div>
        <Button onClick={() => handleOpenModal()} data-testid="add-category-btn">
          <Plus className="h-4 w-4 mr-2" />
          Add Category
        </Button>
      </div>

      <Card>
        <CardHeader>
          <Tabs value={activeTab} onValueChange={setActiveTab}>
            <TabsList>
              <TabsTrigger value="active" className="flex items-center gap-1">
                <Check className="h-4 w-4" />
                Active ({activeCategories.length})
              </TabsTrigger>
              <TabsTrigger value="inactive" className="flex items-center gap-1">
                <X className="h-4 w-4" />
                Inactive ({inactiveCategories.length})
              </TabsTrigger>
            </TabsList>
          </Tabs>
        </CardHeader>
        <CardContent>
          {activeTab === 'active' ? (
            activeCategories.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                <Box className="h-12 w-12 mx-auto mb-3 opacity-50" />
                <p>No active categories</p>
                <Button variant="outline" className="mt-4" onClick={() => handleOpenModal()}>
                  Add your first category
                </Button>
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Name</TableHead>
                    <TableHead>Allowed Relationship Types</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {activeCategories.map((category) => (
                    <TableRow key={category.id}>
                      <TableCell className="font-medium">{category.name}</TableCell>
                      <TableCell>
                        <div className="flex gap-1 flex-wrap">
                          {(category.allowed_relationship_types || ['STANDALONE', 'PARENT', 'CHILD']).map(type => (
                            <Badge key={type} variant="outline" className="text-xs">
                              {type}
                            </Badge>
                          ))}
                        </div>
                      </TableCell>
                      <TableCell className="text-right">
                        <Button 
                          variant="ghost" 
                          size="icon" 
                          onClick={() => handleOpenModal(category)}
                          data-testid={`edit-category-${category.id}`}
                        >
                          <Edit className="h-4 w-4 text-blue-500" />
                        </Button>
                        <Button 
                          variant="ghost" 
                          size="icon" 
                          onClick={() => handleDeactivate(category)}
                          data-testid={`deactivate-category-${category.id}`}
                        >
                          <X className="h-4 w-4 text-red-500" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )
          ) : (
            inactiveCategories.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                <Box className="h-12 w-12 mx-auto mb-3 opacity-50" />
                <p>No inactive categories</p>
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Name</TableHead>
                    <TableHead>Allowed Relationship Types</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {inactiveCategories.map((category) => (
                    <TableRow key={category.id} className="opacity-60">
                      <TableCell className="font-medium">{category.name}</TableCell>
                      <TableCell>
                        <div className="flex gap-1 flex-wrap">
                          {(category.allowed_relationship_types || ['STANDALONE', 'PARENT', 'CHILD']).map(type => (
                            <Badge key={type} variant="outline" className="text-xs">
                              {type}
                            </Badge>
                          ))}
                        </div>
                      </TableCell>
                      <TableCell className="text-right">
                        <Button 
                          variant="ghost" 
                          size="sm" 
                          onClick={() => handleReactivate(category)}
                          className="text-green-600"
                        >
                          <Check className="h-4 w-4 mr-1" />
                          Reactivate
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )
          )}
        </CardContent>
      </Card>

      {/* Add/Edit Category Modal */}
      <Dialog open={showModal} onOpenChange={handleCloseModal}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>{editingCategory ? 'Edit Category' : 'Add New Category'}</DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div>
              <Label>Category Name *</Label>
              <Input
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="e.g., Laptops, Furniture, Equipment"
                data-testid="category-name-input"
              />
            </div>
            
            <div>
              <Label className="mb-2 block">Allowed Relationship Types</Label>
              <p className="text-xs text-gray-500 mb-3">
                Select which relationship types are allowed for assets in this category.
              </p>
              <div className="space-y-2">
                {['STANDALONE', 'PARENT', 'CHILD'].map(type => (
                  <div key={type} className="flex items-center gap-2">
                    <Checkbox
                      id={`type-${type}`}
                      checked={formData.allowed_relationship_types?.includes(type)}
                      onCheckedChange={() => toggleRelationshipType(type)}
                    />
                    <label htmlFor={`type-${type}`} className="text-sm cursor-pointer">
                      {type}
                    </label>
                  </div>
                ))}
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={handleCloseModal}>
              Cancel
            </Button>
            <Button onClick={handleSubmit} data-testid="save-category-btn">
              {editingCategory ? 'Update' : 'Create'} Category
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
